// import React, { useRef, useState } from "react";
// import { Button, Container, Card } from "../../components/atoms";
// import { useNavigate } from "react-router-dom";
// import { useScrollToSection } from "../../hooks";

// // NOTE: Following the same asset convention used on the "How It Works" page
// // (assets/images/howitworks/...). Drop the final illustrations into
// // assets/images/adr/ with these filenames, or swap the paths below.
// // import adrHero from "../../assets/images/adr/adr-hero.svg";
// // import adrTabsIllustration from "../../assets/images/adr/adr-process.svg";

// export const Adr = () => {
//   const [activeService, setActiveService] = useState<number>(0);

//   const seeHowRef = useRef<HTMLDivElement>(null);
//   const getStartRef = useRef<HTMLDivElement>(null);

//   const navigate = useNavigate();
//   useScrollToSection(getStartRef, "getStart");

//   const handleScrollToSeeHow = () => {
//     if (seeHowRef?.current) {
//       seeHowRef.current.scrollIntoView({ behavior: "smooth" });
//     }
//   };

//   const handleScrollToForm = () => {
//     if (getStartRef?.current) {
//       getStartRef.current.scrollIntoView({ behavior: "smooth" });
//     }
//   };

//   // ---------------- Form state ----------------
//   const initialFormState = {
//     fullName: "",
//     email: "",
//     phone: "",
//     country: "",
//     disputeDetails: "",
//     disputeTypes: [] as string[],
//     otherDisputeType: "",
//     urgency: "",
//     claimAmount: "",
//     agreedToPrivacy: false,
//   };

//   const [formData, setFormData] = useState(initialFormState);

//   const countryOptions = [
//     "India",
//     "USA",
//     "UK",
//     "UAE",
//     "Singapore",
//     "Australia",
//     "Canada",
//     "Other",
//   ];

//   const disputeTypeOptions = [
//     "Loan Default / Recovery",
//     "Payment Restructuring",
//     "NBFC / Digital Lending",
//     "Commercial / Trade Finance",
//     "Real Estate / Mortgage",
//     "Consumer Finance",
//     "Other",
//   ];

//   const urgencyOptions = [
//     { label: "Urgent (within 1-2 months)", value: "urgent" },
//     { label: "High Priority (3-6 months)", value: "high" },
//     { label: "Standard (flexible timeline)", value: "standard" },
//   ];

//   const handleDisputeTypeToggle = (option: string) => {
//     setFormData((prev) => {
//       const exists = prev.disputeTypes.includes(option);
//       return {
//         ...prev,
//         disputeTypes: exists
//           ? prev.disputeTypes.filter((item) => item !== option)
//           : [...prev.disputeTypes, option],
//       };
//     });
//   };

//   const handleSubmit = () => {
//     if (!formData.fullName || !formData.email || !formData.phone || !formData.country) {
//       return;
//     }
//     if (!formData.agreedToPrivacy) {
//       return;
//     }
//     // TODO: wire this up to the existing lead-capture / CRM endpoint
//     // used elsewhere in the app.
//     console.log("ADR dispute assessment submitted:", formData);
//   };

//   const handleClearForm = () => {
//     setFormData(initialFormState);
//   };

//   // ---------------- Static content ----------------
//   const whyStats = [
//     {
//       value: "INR 20L+ Cr",
//       label: "Active lending handled annually across India's financial ecosystem",
//     },
//     {
//       value: "3-5 Yrs",
//       label: "Average time drained by traditional litigation",
//     },
//     {
//       value: "INR 10-50L",
//       label: "Typical legal fees incurred in a contested court case",
//     },
//   ];

//   const adrBenefits = [
//     {
//       title: "30-70% Faster Resolution",
//       desc: "6-18 months, compared to 3-5 years in litigation.",
//     },
//     {
//       title: "60-80% Lower Cost",
//       desc: "Minimal legal spend, with no protracted appeals.",
//     },
//     {
//       title: "Confidential Settlements",
//       desc: "No public judgment, and a preserved reputation for both sides.",
//     },
//     {
//       title: "Enforceable Outcomes",
//       desc: "Final awards under the Arbitration and Conciliation Act, 1996 and Mediation Act, 2023.",
//     },
//   ];

//   const adminSupport = [
//     "Case management & appointment coordination",
//     "Scheduling and documentation",
//     "Communications with all parties",
//     "Virtual hearings",
//     "Record maintenance",
//     "Settlement administration",
//   ];

//   const services = [
//     {
//       tab: "Negotiation",
//       tagline: "The Direct Conversation",
//       intro:
//         "For when parties want control and speed. Negotiation is unmediated direct dialogue between parties (and their counsel). Solveesy doesn't intervene — we facilitate the logistics and ensure structured discussion:",
//       points: [
//         "Both parties agree to negotiate in good faith",
//         "Defined discussion topics and timeline",
//         "Confidential exchanges without prejudice",
//         "Settlement reached by mutual agreement",
//       ],
//       bestForTitle: "Best for:",
//       bestFor: [
//         "Straightforward payment disputes",
//         "Quick resolution within 2-4 weeks",
//         "When parties have an existing relationship and trust",
//         "Small to mid-sized claims",
//       ],
//     },
//     {
//       tab: "Conciliation",
//       tagline: "The Structured Middle Path",
//       intro:
//         "For when negotiation stalls but parties are willing to compromise. Conciliation is facilitated negotiation where a neutral conciliator actively participates — but doesn't decide. The conciliator:",
//       points: [
//         "Listens to both positions",
//         "Identifies common ground",
//         "Suggests settlement options (parties aren't bound by suggestions)",
//         "Drafts settlement agreements",
//       ],
//       bestForTitle: "Best for:",
//       bestFor: [
//         "Payment plan restructuring disputes",
//         "Collateral valuation disagreements",
//         "Cross-claims between borrowers and lenders",
//         "Cases where parties are receptive but need guidance",
//       ],
//     },
//     {
//       tab: "Mediation",
//       tagline: "The Dialogue Solution",
//       intro:
//         "For when parties want expert facilitation toward mutual agreement. Mediation is structured dialogue facilitated by a neutral mediator who coaches parties toward resolution — but does not decide. Mediators are expert listeners who:",
//       points: [
//         "Understand each party's interests, not just positions",
//         "Identify creative settlement options",
//         "Build confidence in agreements",
//         "Remain impartial and confidential",
//       ],
//       bestForTitle: "Our mediators specialise in:",
//       bestFor: [
//         "Loan restructuring negotiations",
//         "NPA settlement agreements",
//         "Payment moratoriums and modified EMI plans",
//         "Collateral substitution or release",
//         "Asset securitisation disputes",
//       ],
//     },
//     {
//       tab: "Arbitration",
//       tagline: "The Decisive Path",
//       intro:
//         "For disputes needing a final, binding resolution. When parties cannot settle, arbitration provides a binding award — final, enforceable, no appeal on merit. Our arbitrators are:",
//       points: [
//         "Retired judges and senior counsel",
//         "Financial services specialists",
//         "Insolvency and credit experts",
//         "Trained in commercial arbitration",
//       ],
//       bestForTitle: "Solveesy arbitration covers:",
//       bestFor: [
//         "Loan agreement disputes (default, penalty clauses, prepayment)",
//         "Credit facility disputes (disbursement, covenant breach)",
//         "Cross-claims between co-borrowers and lenders",
//         "Recovery enforcement and asset realisation",
//         "Trade and commercial disputes with financial dimensions",
//       ],
//     },
//     {
//       tab: "ODR",
//       tagline: "The Digital Advantage",
//       intro:
//         "All ADR mechanisms, entirely online. For micro and small disputes — digital lending defaults, marketplace disputes, consumer loans, API-based loan products — Solveesy ODR enables full-cycle resolution without physical meetings. ODR delivery includes:",
//       points: [
//         "Virtual negotiation & conciliation sessions (video-based)",
//         "Digital mediation (Zoom / secure video platform)",
//         "Online arbitration hearings",
//       ],
//       bestForTitle: "Also includes:",
//       bestFor: [
//         "Digital evidence submission (documents, images, recordings)",
//         "E-signature on settlements",
//         "Electronically issued awards",
//       ],
//     },
//   ];

//   return (
//     <>
//       {/* Hero */}
//       <div className="md:my-20 px-4 flex flex-col md:flex-row justify-between">
//         <div className="flex-col w-full md:w-1/2 flex justify-center items-center md:items-start text-center md:text-left">
//           <div className="mt-11 font-bold">
//             <h1 className="text-4xl md:text-[54px] font-medium leading-snug">
//               <span className="text-[#015CFD]">ADR, ODR</span> &amp; Mediation
//             </h1>
//             <h1 className="text-4xl md:text-[54px] font-medium leading-snug">
//               Institutional Services in <span className="text-[#015CFD]">India</span>
//             </h1>

//             <div className="text-[#464646] w-full md:w-3/4 my-3 font-normal text-sm md:text-base">
//               Discover how Alternative Dispute Resolution transforms financial
//               recovery into collaborative settlement — through mediation,
//               arbitration, and online dispute resolution.
//             </div>
//           </div>

//           <div className="w-full mt-8 flex justify-center md:justify-start gap-4">
//             <Button
//               className="w-fit text-[#FFFFFF] bg-[#015cfd] text-white text-base font-normal"
//               type="button"
//               onClick={handleScrollToSeeHow}
//             >
//               Why Solveesy
//             </Button>
//             <Button
//               className="w-fit px-7 bg-[#E7E7E7] text-[#101010] text-base font-normal"
//               type="button"
//               onClick={handleScrollToForm}
//             >
//               Get Your Free Assessment
//             </Button>
//           </div>

//           <div className="w-[200px] h-[200px] md:w-[403px] md:h-[403px] rounded-full bg-[#00C2FF] blur-[100px] md:blur-[200px] absolute -z-10 opacity-25 top-[400px] md:top-[600px] left-0 md:left-[100px]" />
//         </div>

//         <div className="w-full md:w-1/2 flex justify-center md:justify-end mt-10 md:mt-0">
//           {/* <img src={adrHero} alt="ADR, ODR and Mediation services" className="max-w-full h-auto" /> */}
//         </div>
//       </div>

//       {/* Why Solveesy */}
//       <div ref={seeHowRef}>
//         <div className="text-[28px] sm:text-[46px] leading-snug font-bold pt-10 px-4 sm:px-0 sm:w-1/2 md:my-10">
//           Why <span className="text-[#015CFD]">Solveesy</span>?
//         </div>

//         <div className="text-[#596780] px-4 sm:px-0 sm:w-2/3">
//           India's financial ecosystem handles over INR 20 lakh crore in active
//           lending annually. Yet traditional litigation drains 3-5 years, INR
//           10-50 lakhs in legal fees, and irreversible relationships. Solveesy
//           changes that equation.
//         </div>

//         <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 px-4 sm:px-0 mt-10">
//           {whyStats.map(({ value, label }, i) => (
//             <Card key={i} className="p-6">
//               <div className="text-[#015CFD] font-bold text-[32px] sm:text-[40px]">
//                 {value}
//               </div>
//               <p className="text-[#949494] mt-2">{label}</p>
//             </Card>
//           ))}
//         </div>

//         <div className="text-[#596780] px-4 sm:px-0 sm:w-2/3 mt-10">
//           We are India's pioneer Alternative Dispute Resolution (ADR) service
//           provider, specialising in transforming loan recovery — and every
//           dispute involving borrowers and lenders — from zero-sum battles
//           into structured, win-win settlements. Through mediation,
//           arbitration, and online dispute resolution (ODR), we help both
//           parties preserve capital, time, and dignity.
//         </div>
//       </div>

//       {/* Legal framework + admin support */}
//       <div className="my-2 md:my-20">
//         <div className="md:my-20 max-sm:mb-[-10%] text-[28px] sm:text-[46px] leading-snug font-bold pt-10 px-4 sm:px-0 sm:w-1/2">
//           What We Do: An <span className="text-[#015CFD]">ADR Service Provider</span>
//         </div>

//         <div className="flex flex-col md:flex-row gap-10 sm:gap-6 px-4 sm:px-0">
//           <div className="flex flex-col sm:basis-1/2">
//             <h1 className="text-[20px] sm:text-[24px] my-2 font-medium text-[#040815]">
//               Grounded in Law
//             </h1>
//             <p className="text-[#596780]">
//               Solveesy operates within the legal framework of India's
//               Arbitration and Conciliation Act, 1996, Mediation Act, 2023 and
//               aligns with the UNCITRAL Model Law on mediation. We deploy
//               trained and certified mediators, arbitrators, and conciliators
//               specialised in financial dispute resolution.
//             </p>
//           </div>

//           <div className="flex flex-col sm:basis-1/2">
//             <h1 className="text-[20px] sm:text-[24px] my-2 font-medium text-[#040815]">
//               End-to-End Administrative Support
//             </h1>
//             <p className="text-[#596780] mb-3">
//               In addition to facilitating dispute resolution, Solveesy
//               provides end-to-end administrative support for ADR
//               proceedings, allowing parties to focus on resolution rather
//               than process:
//             </p>
//             <ul className="text-[#686868] list-disc marker:text-[#015CFD] pl-5">
//               {adminSupport.map((item, i) => (
//                 <li key={i} className="my-2">
//                   {item}
//                 </li>
//               ))}
//             </ul>
//           </div>
//         </div>
//       </div>

//       {/* Five Core ADR Services - tabbed, matching the "Three Stages" pattern */}
//       <div>
//         <div className="text-[28px] sm:text-[46px] mt-12 md:mt-20 leading-snug text-center sm:text-left px-4">
//           <span className="text-[20px] sm:text-[31px] text-[#015CFD] font-medium block">
//             Five Core Services
//           </span>
//           <div className="font-bold">How We Resolve Disputes</div>
//         </div>

//         <div className="flex flex-col-reverse lg:flex-row justify-between items-start px-4 mt-10 gap-6">
//           <div className="w-full lg:w-1/2">
//             <div className="flex flex-wrap justify-start my-4 bg-[#F3F5F7] rounded-full overflow-hidden w-full max-w-full sm:max-w-xl">
//               {services.map((service, index) => (
//                 <button
//                   key={index}
//                   onClick={() => setActiveService(index)}
//                   className={`flex-1 py-2 px-3 text-sm font-medium transition-all duration-300 whitespace-nowrap
//                     ${
//                       activeService === index
//                         ? "bg-[#015CFD] text-[#FFF]"
//                         : "bg-[#F3F5F7] text-[#90A3BF]"
//                     }
//                     rounded-none`}
//                   style={{ borderRadius: "30px" }}
//                 >
//                   {service.tab}
//                 </button>
//               ))}
//             </div>

//             <div className="text-[20px] sm:text-[24px] mt-[30px] font-medium">
//               {services[activeService].tab}
//               <span className="text-[#015CFD]"> — {services[activeService].tagline}</span>
//             </div>

//             <div className="text-[#333] mt-3 text-[16px] sm:text-[18px]">
//               {services[activeService].intro}
//             </div>
//             <ul className="text-[#686868] text-[16px] sm:text-[18px] my-5 list-disc marker:text-[#015CFD] pl-5">
//               {services[activeService].points.map((point, i) => (
//                 <li key={i} className="my-3">
//                   {point}
//                 </li>
//               ))}
//             </ul>

//             <div className="font-medium text-[#040815] mt-6">
//               {services[activeService].bestForTitle}
//             </div>
//             <ul className="text-[#686868] text-[16px] sm:text-[18px] my-3 list-disc marker:text-[#015CFD] pl-5">
//               {services[activeService].bestFor.map((point, i) => (
//                 <li key={i} className="my-2">
//                   {point}
//                 </li>
//               ))}
//             </ul>
//           </div>

//           <div className="w-full lg:w-auto">
//             {/* <img
//               className="w-full max-w-sm sm:max-w-md lg:max-w-none mr-0 lg:mr-4 h-auto"
//               src={adrTabsIllustration}
//               alt={services[activeService].tab}
//             /> */}
//           </div>
//         </div>
//       </div>

//       {/* Benefits grid - matching the "Core Values" 2x2 pattern */}
//       <div>
//         <div className="flex justify-center font-bold mt-6 md:mt-28 text-center px-4">
//           <div className="text-[28px] sm:text-[46px]">
//             Why ADR <span className="text-[#015CFD]">Works</span>
//           </div>
//         </div>

//         <div className="grid grid-cols-1 sm:grid-cols-2 mt-10 gap-6 px-4">
//           {adrBenefits.map(({ title, desc }, i) => (
//             <Card key={i} className="p-6 flex items-start gap-4">
//               <div className="bg-[#015CFD] p-3 rounded-md flex-shrink-0">
//                 <svg
//                   className="w-6 h-6"
//                   viewBox="0 0 24 24"
//                   fill="none"
//                   xmlns="http://www.w3.org/2000/svg"
//                 >
//                   <path
//                     d="M5 13l4 4L19 7"
//                     stroke="#FFFFFF"
//                     strokeWidth="2"
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                   />
//                 </svg>
//               </div>
//               <div>
//                 <h1 className="font-bold text-lg sm:text-xl">{title}</h1>
//                 <p className="text-[16px] mt-2 text-[#949494]">{desc}</p>
//               </div>
//             </Card>
//           ))}
//         </div>
//       </div>

//       {/* Get in touch form */}
      
             
     
//     </>
//   );
// };

// export default Adr;

import React, { useRef, useState } from "react";
import { Button, Container, Card } from "../../components/atoms";
import { useNavigate } from "react-router-dom";
import { useScrollToSection } from "../../hooks";

const Adr = () => {
  const [activeService, setActiveService] = useState<number>(0);

  const seeHowRef = useRef<HTMLDivElement>(null);
  const getStartRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  useScrollToSection(getStartRef, "getStart");

  const handleScrollToSeeHow = () => {
    if (seeHowRef?.current) {
      seeHowRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleScrollToForm = () => {
    if (getStartRef?.current) {
      getStartRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // ---------------- Form state ----------------
  const initialFormState = {
    fullName: "",
    email: "",
    phone: "",
    country: "",
    disputeDetails: "",
    disputeTypes: [] as string[],
    otherDisputeType: "",
    urgency: "",
    claimAmount: "",
    agreedToPrivacy: false,
  };

  const [formData, setFormData] = useState(initialFormState);

  const countryOptions = [
    "India",
    "USA",
    "UK",
    "UAE",
    "Singapore",
    "Australia",
    "Canada",
    "Other",
  ];

  const disputeTypeOptions = [
    "Loan Default / Recovery",
    "Payment Restructuring",
    "NBFC / Digital Lending",
    "Commercial / Trade Finance",
    "Real Estate / Mortgage",
    "Consumer Finance",
    "Other",
  ];

  const urgencyOptions = [
    { label: "Urgent (within 1-2 months)", value: "urgent" },
    { label: "High Priority (3-6 months)", value: "high" },
    { label: "Standard (flexible timeline)", value: "standard" },
  ];

  const handleDisputeTypeToggle = (option: string) => {
    setFormData((prev) => {
      const exists = prev.disputeTypes.includes(option);
      return {
        ...prev,
        disputeTypes: exists
          ? prev.disputeTypes.filter((item) => item !== option)
          : [...prev.disputeTypes, option],
      };
    });
  };

  const handleSubmit = () => {
    if (!formData.fullName || !formData.email || !formData.phone || !formData.country) {
      return;
    }
    if (!formData.agreedToPrivacy) {
      return;
    }
    // TODO: wire this up to the existing lead-capture / CRM endpoint
    // used elsewhere in the app.
    console.log("ADR dispute assessment submitted:", formData);
  };

  const handleClearForm = () => {
    setFormData(initialFormState);
  };

  // ---------------- Static content ----------------
  const whyStats = [
    {
      value: "INR 20L+ Cr",
      label: "Active lending handled annually across India's financial ecosystem",
    },
    {
      value: "3-5 Yrs",
      label: "Average time drained by traditional litigation",
    },
    {
      value: "INR 10-50L",
      label: "Typical legal fees incurred in a contested court case",
    },
  ];

  const adrBenefits = [
    {
      title: "30-70% Faster Resolution",
      desc: "6-18 months, compared to 3-5 years in litigation.",
    },
    {
      title: "60-80% Lower Cost",
      desc: "Minimal legal spend, with no protracted appeals.",
    },
    {
      title: "Confidential Settlements",
      desc: "No public judgment, and a preserved reputation.",
    },
    {
      title: "Enforceable Outcomes",
      desc: "Final awards under the Arbitration and Conciliation Act, 1996 and Mediation Act, 2023.",
    },
  ];

  const adminSupport = [
    "Case management & appointment coordination",
    "Scheduling and documentation",
    "Communications with all parties",
    "Virtual hearings",
    "Record maintenance",
    "Settlement administration",
  ];

  const services = [
    {
      tab: "Negotiation",
      tagline: "The Direct Conversation",
      intro:
        "For when parties want control and speed. Negotiation is unmediated direct dialogue between parties (and their counsel). Solveesy doesn't intervene — we facilitate the logistics and ensure structured discussion:",
      points: [
        "Both parties agree to negotiate in good faith",
        "Defined discussion topics and timeline",
        "Confidential exchanges without prejudice",
        "Settlement reached by mutual agreement",
      ],
      bestForTitle: "Best for:",
      bestFor: [
        "Straightforward payment disputes",
        "Quick resolution within 2-4 weeks",
        "When parties have an existing relationship and trust",
        "Small to mid-sized claims",
      ],
    },
    {
      tab: "Conciliation",
      tagline: "The Structured Middle Path",
      intro:
        "For when negotiation stalls but parties are willing to compromise. Conciliation is facilitated negotiation where a neutral conciliator actively participates — but doesn't decide. The conciliator:",
      points: [
        "Listens to both positions",
        "Identifies common ground",
        "Suggests settlement options (parties aren't bound by suggestions)",
        "Drafts settlement agreements",
      ],
      bestForTitle: "Best for:",
      bestFor: [
        "Payment plan restructuring disputes",
        "Collateral valuation disagreements",
        "Cross-claims between borrowers and lenders",
        "Cases where parties are receptive but need guidance",
      ],
    },
    {
      tab: "Mediation",
      tagline: "The Dialogue Solution",
      intro:
        "For when parties want expert facilitation toward mutual agreement. Mediation is structured dialogue facilitated by a neutral mediator who coaches parties toward resolution — but does not decide. Mediators are expert listeners who:",
      points: [
        "Understand each party's interests (not just positions)",
        "Identify creative settlement options",
        "Build confidence in agreements",
        "Remain impartial and confidential",
      ],
      bestForTitle: "Our mediators specialise in:",
      bestFor: [
        "Loan restructuring negotiations",
        "NPA settlement agreements",
        "Payment moratoriums and modified EMI plans",
        "Collateral substitution or release",
        "Asset securitisation disputes",
      ],
    },
    {
      tab: "Arbitration",
      tagline: "The Decisive Path",
      intro:
        "For disputes needing a final, binding resolution. When parties cannot settle, arbitration provides a binding award — final, enforceable, no appeal on merit. Our arbitrators are:",
      points: [
        "Retired judges and senior counsel",
        "Financial services specialists",
        "Insolvency and credit experts",
        "Trained in commercial arbitration",
      ],
      bestForTitle: "Solveesy arbitration covers:",
      bestFor: [
        "Loan agreement disputes (default, penalty clauses, prepayment)",
        "Credit facility disputes (disbursement, covenant breach)",
        "Cross-claims between co-borrowers and lenders",
        "Recovery enforcement and asset realisation",
        "Trade and commercial disputes with financial dimensions",
      ],
    },
    {
      tab: "ODR",
      tagline: "The Digital Advantage",
      intro:
        "All ADR mechanisms, entirely online. For micro and small disputes — digital lending defaults, marketplace disputes, consumer loans, API-based loan products — Solveesy ODR enables full-cycle resolution without physical meetings. ODR delivery includes:",
      points: [
        "Virtual negotiation & conciliation sessions (video-based)",
        "Digital mediation (Zoom / secure video platform)",
        "Online arbitration hearings",
      ],
      bestForTitle: "Also includes:",
      bestFor: [
        "Digital evidence submission (documents, images, recordings)",
        "E-signature on settlements",
        "Electronically issued awards",
      ],
    },
  ];

  return (
    <>
      {/* Hero */}
      <div className="md:my-20 px-4 flex flex-col justify-center items-center text-center">
        <div className="mt-11 font-bold max-w-4xl">
          <h1 className="text-4xl md:text-[54px] font-medium leading-snug">
            <span className="text-[#015CFD]">ADR, ODR</span> &amp; Mediation
          </h1>
          <h1 className="text-4xl md:text-[54px] font-medium leading-snug">
            Institutional Services in <span className="text-[#015CFD]">India</span>
          </h1>

          <div className="text-[#464646] w-full my-3 font-normal text-sm md:text-base max-w-2xl mx-auto">
            Discover how Alternative Dispute Resolution transforms financial
            recovery into collaborative settlement — through mediation,
            arbitration, and online dispute resolution.
          </div>
        </div>

        <div className="w-full mt-8 flex justify-center gap-4">
          <Button
            className="w-fit text-[#FFFFFF] bg-[#015cfd] text-white text-base font-normal"
            type="button"
            onClick={handleScrollToSeeHow}
          >
            Why Solveesy
          </Button>
          <Button
            className="w-fit px-7 bg-[#E7E7E7] text-[#101010] text-base font-normal"
            type="button"
            onClick={handleScrollToForm}
          >
            Get Your Free Assessment
          </Button>
        </div>

        <div className="w-[200px] h-[200px] md:w-[403px] md:h-[403px] rounded-full bg-[#00C2FF] blur-[100px] md:blur-[200px] absolute -z-10 opacity-25 top-[400px] md:top-[600px] left-0 md:left-[100px]" />
      </div>

      {/* Why Solveesy */}
      <div ref={seeHowRef}>
        <div className="text-[28px] sm:text-[46px] leading-snug font-bold pt-10 px-4 sm:px-0 md:my-10">
          Why <span className="text-[#015CFD]">Solveesy</span>?
        </div>

        <div className="text-[#596780] px-4 sm:px-0 w-full space-y-5">
          <p>
            India's financial ecosystem handles over INR 20 lakh crore in
            active lending annually. Yet traditional litigation drains 3-5
            years, INR 10-50 lakhs in legal fees, and irreversible
            relationships. Solveesy changes that equation.
          </p>
          <p>
            We are India's pioneer Alternative Dispute Resolution (ADR)
            service provider, specialising in transforming loan recovery—and
            every dispute involving borrowers and lenders—from zero-sum
            battles into structured, win-win settlements. Through mediation,
            arbitration, and online dispute resolution (ODR), we help both
            parties preserve capital, time, and dignity.
          </p>
          <p>
            What makes Solveesy unique is our deep understanding of both
            sides of the equation: financial recovery and ADR services. We
            understand the commercial realities of recovery while leveraging
            modern ADR mechanisms—including mediation, conciliation,
            arbitration, negotiation, and ODR—to create practical, legally
            sound, and mutually beneficial outcomes.
          </p>
        </div>

        {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 px-4 sm:px-0 mt-10">
          {whyStats.map(({ value, label }, i) => (
            <Card key={i} className="p-6">
              <div className="text-[#015CFD] font-bold text-[32px] sm:text-[40px]">
                {value}
              </div>
              <p className="text-[#949494] mt-2">{label}</p>
            </Card>
          ))}
        </div> */}
      </div>

      {/* What We Do: Solveesy is an ADR Service Provider */}
     <div className="my-2 md:my-20">
        <div className="mb-6 md:mb-10 text-[28px] sm:text-[46px] leading-snug font-bold pt-10 px-4 sm:px-0 sm:w-3/4">
          What We Do: Solveesy is an <span className="text-[#015CFD]">ADR Service Provider</span>
        </div>

        <div className="px-4 sm:px-0 w-full">
          <p className="text-[#596780]">
            Alternative Dispute Resolution (ADR) provides proven alternatives
            to traditional court litigation. For lenders, borrowers, and
            financial institutions, ADR means:
          </p>

          <ul className="text-[#686868] text-[16px] sm:text-[18px] my-5 list-disc marker:text-[#015CFD] pl-5">
            {adrBenefits.map(({ title, desc }, i) => (
              <li key={i} className="my-3">
                <span className="font-medium text-[#040815]">{title}</span>{" "}
                — {desc}
              </li>
            ))}
          </ul>

          <p className="text-[#596780]">
            Solveesy operates within the legal framework of India's
            Arbitration and Conciliation Act, 1996, Mediation Act, 2023 and
            aligns with UNCITRAL Model Law on mediation. We deploy trained
            and certified mediators, arbitrators, and conciliators
            specialised in financial dispute resolution.
          </p>

          <p className="text-[#596780] mt-5 mb-3">
            In addition to facilitating dispute resolution, Solveesy provides
            end-to-end administrative support for ADR proceedings—including
            case management, appointment coordination, scheduling,
            documentation, communications, virtual hearings, record
            maintenance, and settlement administration—allowing parties to
            focus on resolution rather than process:
          </p>
          {/* <ul className="text-[#686868] list-disc marker:text-[#015CFD] pl-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {adminSupport.map((item, i) => (
              <li key={i} className="my-2">
                {item}
              </li>
            ))}
          </ul> */}
        </div>
      </div>

      {/* Five Core ADR Services - tabbed, matching the "Three Stages" pattern */}
      <div>
        <div className="text-[28px] sm:text-[46px] mt-12 md:mt-20 leading-snug text-center sm:text-left px-4">
          <span className="text-[20px] sm:text-[31px] text-[#015CFD] font-medium block">
            Five Core Services
          </span>
          <div className="font-bold">How We Resolve Disputes</div>
        </div>

        <div className="px-4 mt-10">
          <div className="w-full">
            <div className="flex flex-wrap justify-start my-4 bg-[#F3F5F7] rounded-full overflow-hidden w-full max-w-full sm:max-w-xl">
              {services.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`flex-1 py-2 px-3 text-sm font-medium transition-all duration-300 whitespace-nowrap
                    ${
                      activeService === index
                        ? "bg-[#015CFD] text-[#FFF]"
                        : "bg-[#F3F5F7] text-[#90A3BF]"
                    }
                    rounded-none`}
                  style={{ borderRadius: "30px" }}
                >
                  {service.tab}
                </button>
              ))}
            </div>

            <div className="text-[20px] sm:text-[24px] mt-[30px] font-medium">
              {services[activeService].tab}
              <span className="text-[#015CFD]"> — {services[activeService].tagline}</span>
            </div>

            <div className="text-[#333] mt-3 text-[16px] sm:text-[18px]">
              {services[activeService].intro}
            </div>
            <ul className="text-[#686868] text-[16px] sm:text-[18px] my-5 list-disc marker:text-[#015CFD] pl-5">
              {services[activeService].points.map((point, i) => (
                <li key={i} className="my-3">
                  {point}
                </li>
              ))}
            </ul>

            <div className="font-medium text-[#040815] mt-6">
              {services[activeService].bestForTitle}
            </div>
            <ul className="text-[#686868] text-[16px] sm:text-[18px] my-3 list-disc marker:text-[#015CFD] pl-5">
              {services[activeService].bestFor.map((point, i) => (
                <li key={i} className="my-2">
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Benefits grid - matching the "Core Values" 2x2 pattern */}
      <div>
        <div className="flex justify-center font-bold mt-6 md:mt-28 text-center px-4">
          <div className="text-[28px] sm:text-[46px]">
            Why ADR <span className="text-[#015CFD]">Works</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 mt-10 gap-6 px-4">
          {adrBenefits.map(({ title, desc }, i) => (
            <Card key={i} className="p-6 flex items-start gap-4">
              <div className="bg-[#015CFD] p-3 rounded-md flex-shrink-0">
                <svg
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 13l4 4L19 7"
                    stroke="#FFFFFF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <h1 className="font-bold text-lg sm:text-xl">{title}</h1>
                <p className="text-[16px] mt-2 text-[#949494]">{desc}</p>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Get in touch form */}
      

             
    </>
  );
};

export default Adr;